import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ADMIN_EMAILS = [process.env.REACT_APP_ADMIN_EMAIL, 'jack@thehubcrookwell.com.au'].filter(Boolean);

const HUB_LOGO = () => (
  <img src="/images/mainlogo.jpg" alt="The Hub Crookwell" className="h-16 w-auto" />
);

export default function Navbar() {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const isAdmin = currentUser && (ADMIN_EMAILS.includes(currentUser.email) || userProfile?.role === 'admin');

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setProfileOpen(false);
  };

  const navLinkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${
      isActive ? 'text-brand-600' : 'text-slate-600 hover:text-slate-900'
    }`;

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <HUB_LOGO />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-7">
            <NavLink to="/spaces" className={navLinkClass}>Spaces</NavLink>
            <NavLink to="/book" className={navLinkClass}>Book Now</NavLink>
          </div>

          {/* Auth buttons */}
          <div className="hidden md:flex items-center gap-3">
            {currentUser ? (
              <div className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-semibold text-sm">
                    {currentUser.displayName?.charAt(0) || currentUser.email?.charAt(0) || 'M'}
                  </div>
                  <span>{currentUser.displayName?.split(' ')[0] || 'Member'}</span>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                    <Link
                      to="/dashboard"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-gray-50"
                      onClick={() => setProfileOpen(false)}
                    >
                      My Dashboard
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="block px-4 py-2 text-sm text-purple-600 hover:bg-purple-50"
                        onClick={() => setProfileOpen(false)}
                      >
                        Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn-secondary text-sm px-4 py-2">Sign In</Link>
                <Link to="/book" className="btn-primary text-sm px-4 py-2">Book a Space</Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-gray-100"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-2">
          <NavLink to="/spaces" className="block py-2 text-sm font-medium text-slate-700" onClick={() => setMobileOpen(false)}>Spaces</NavLink>
          <NavLink to="/book" className="block py-2 text-sm font-medium text-slate-700" onClick={() => setMobileOpen(false)}>Book Now</NavLink>
          {currentUser ? (
            <>
              <NavLink to="/dashboard" className="block py-2 text-sm font-medium text-slate-700" onClick={() => setMobileOpen(false)}>My Dashboard</NavLink>
              {isAdmin && (
                <NavLink to="/admin" className="block py-2 text-sm font-medium text-purple-600" onClick={() => setMobileOpen(false)}>Admin Panel</NavLink>
              )}
              <button onClick={handleLogout} className="block w-full text-left py-2 text-sm font-medium text-red-600">Sign Out</button>
            </>
          ) : (
            <div className="flex gap-2 pt-2">
              <Link to="/login" className="btn-secondary text-sm flex-1 text-center" onClick={() => setMobileOpen(false)}>Sign In</Link>
              <Link to="/book" className="btn-primary text-sm flex-1 text-center" onClick={() => setMobileOpen(false)}>Book Now</Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
